import React from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import { CircularProgress, Divider } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import { RoundedButton } from 'common/Button/Button'
import NormalText from 'common/Typography/NormalText'
import { TransformedFaberAndGlobalUsersType } from 'pages/OTPacks/Global/components/UserProfileV3'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'

interface Props {
  handleCreateRequestDialogClose: () => void
  faberToken: string
  createDeleteRequestDialogOpen: boolean
  customerRow: SubscriptionsTableRows | TransformedFaberAndGlobalUsersType
}

export default function CreateDeleteRequestDialog({
  faberToken,
  createDeleteRequestDialogOpen,
  handleCreateRequestDialogClose,
  customerRow
}: Props) {
  const queryClient = useQueryClient()
  const { mutate: createDeleteRequest, isLoading: isCreateRequestLoading } =
    useMutation(EsimAPI.createDeleteRequest, {
      onSuccess: (res) => {
        if (res.data.status === 'OK') {
          toast.success('Delete request created successfully')
          queryClient.invalidateQueries(['delete-request', customerRow.userID])
        } else {
          toast.error('Failed to create delete request')
        }
      },
      onError: () => {
        toast.error(
          'There was an error creating the delete request, please try again.'
        )
      },
      onSettled: () => {
        handleCreateRequestDialogClose()
      }
    })

  const userId = customerRow.userID

  const handleConfirm = () => {
    createDeleteRequest({
      userId: userId || '',
      faberToken,
      status: 'PENDING_DELETION'
    })
  }

  return (
    <Dialog
      open={createDeleteRequestDialogOpen}
      onClose={handleCreateRequestDialogClose}
    >
      <DialogTitle id='alert-dialog-title'>
        {'Create Delete Request'}
      </DialogTitle>
      <Divider sx={{ width: '100%' }} />
      <DialogContent>
        <NormalText sx={{ p: 0 }}>
          {`This will create an account deletion request for the customer. Proceed if you wish to mark the account for deletion. The request will be automatically set to "PENDING_DELETION".`}
        </NormalText>
      </DialogContent>
      <DialogActions sx={{ mb: '1rem' }}>
        <RoundedButton onClick={handleCreateRequestDialogClose}>
          Cancel
        </RoundedButton>
        <LoadingButton
          loading={isCreateRequestLoading}
          variant='filled'
          onClick={handleConfirm}
          loadingIndicator={<CircularProgress color='inherit' size={16} />}
          autoFocus
        >
          Create Delete Request
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
